import { SharePropertyPicker } from 'src/modules/shared/components/molecules/share-property-picker';
import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import type { Template } from 'src/api/generated';
import { useThrottle } from '@uidotdev/usehooks';
import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { useTextareaInsertion } from 'src/modules/shared/hooks/use-textarea-insertion';
import { useTextareaKeyboard } from 'src/modules/shared/hooks/use-textarea-keyboard';
import { useBackgroundFocusRetention } from 'src/modules/shared/hooks/use-background-focus-retention';
import { useTemplateSuggestions } from '../../../hooks/templates/suggestions';
import TemplateSuggestions from '../templates/template-suggestions';
import TemplateModal from '../../organisms/templates/template-modal';
import type {
  MessageInputProps,
  MessageInputRef,
} from '../../../types/message-input.types';

const SEARCH_THROTTLE_MS = 1000;

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  (
    {
      value,
      onChange,
      onSendMessage,
      isSendingMessage = false,
      disabled = false,
      // TODO: check when implementing i18n
      placeholder = 'Escribe / para buscar plantillas ⚡, o escribe tu mensaje...',
      renderSendButton,
      onPaste,
      enterBehavior = 'send',
      rows = 3,
      maxRows,
      heightBoundary,
      focusRetentionBoundary,
      suggestionsSuspended = false,
      onFocus,
    },
    ref
  ) => {
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isPropertyPickerOpen, setIsPropertyPickerOpen] = useState(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useBackgroundFocusRetention(textAreaRef, focusRetentionBoundary, !disabled);
    const selection = useRef({ start: 0, end: 0 });
    const throttledMessage = useThrottle(value, SEARCH_THROTTLE_MS);
    const insertion = useTextareaInsertion({
      inputRef: textAreaRef,
      value,
      onChange,
      disabled,
    });
    const { hideKeyboard, resumeKeyboard } = useTextareaKeyboard({
      inputRef: textAreaRef,
      disabled,
      dismissOnKeyboardHide: Boolean(focusRetentionBoundary),
    });
    const { focus, captureSelection } = insertion;

    useImperativeHandle(
      ref,
      () => ({
        openPropertyPicker: () => {
          selection.current = {
            start: textAreaRef.current?.selectionStart ?? value.length,
            end: textAreaRef.current?.selectionEnd ?? value.length,
          };
          setIsPropertyPickerOpen(true);
        },
        focus: () => {
          resumeKeyboard();
          focus();
        },
        hideKeyboard: () => captureSelection() && hideKeyboard(),
        setCursorPosition: insertion.setCursorPosition,
        captureSelection,
        insertText: insertion.insertText,
      }),
      [
        value,
        focus,
        insertion.setCursorPosition,
        captureSelection,
        insertion.insertText,
        hideKeyboard,
        resumeKeyboard,
      ]
    );

    const handleTemplateSelect = (template: Template) => {
      const words = value.split(' ');
      words.pop();
      const newMessage = [...words, template.content].join(' ');
      onChange(newMessage);
    };

    const {
      isOpen: isSuggestionsOpen,
      suggestions,
      selectedIndex,
      loading: isLoadingSuggestions,
      hasMorePages,
      handleKeyDown,
      fetchMore,
      handleClose,
    } = useTemplateSuggestions({
      inputValue: throttledMessage,
      onSelect: handleTemplateSelect,
      enabled: !suggestionsSuspended,
    });

    const showSuggestions = isSuggestionsOpen && !suggestionsSuspended;
    useCloseRequest({ isOpen: showSuggestions, onClose: handleClose });

    const handleSubmit = () => {
      if (!value.trim() || isSendingMessage || disabled) return;

      onSendMessage(value.trim());
    };

    return (
      <>
        <div className="min-w-0 flex-1 relative">
          <div className="flex min-w-0 gap-2">
            <TextArea
              ref={textAreaRef}
              value={value}
              onChange={(e) => {
                insertion.captureSelection();
                onChange(e.target.value);
              }}
              onSelect={insertion.captureSelection}
              onBlur={insertion.captureSelection}
              onFocus={onFocus}
              onCompositionStart={insertion.onCompositionStart}
              onCompositionEnd={insertion.onCompositionEnd}
              onKeyDown={(e) => {
                if (
                  e.nativeEvent.isComposing ||
                  e.keyCode === 229 ||
                  insertion.isComposing()
                ) {
                  return;
                }
                if (showSuggestions) {
                  handleKeyDown(e);
                } else if (
                  enterBehavior === 'send' &&
                  e.key === 'Enter' &&
                  !e.shiftKey
                ) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onPaste={onPaste}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              autoGrow={maxRows !== undefined}
              maxRows={maxRows}
              heightBoundary={heightBoundary}
              className={maxRows === undefined ? 'flex-1' : 'flex-none'}
            />
            {renderSendButton?.({
              onClick: handleSubmit,
              disabled: !value.trim() || isSendingMessage || disabled,
            })}
          </div>

          <SharePropertyPicker
            disabled={disabled || isSendingMessage}
            open={isPropertyPickerOpen}
            onClose={() => setIsPropertyPickerOpen(false)}
            onInsert={(url) => {
              const { start, end } = selection.current;
              const prefix = value.slice(0, start);
              const suffix = value.slice(end);
              const inserted = `${prefix && !/\s$/u.test(prefix) ? ' ' : ''}${url}${suffix && !/^\s/u.test(suffix) ? ' ' : ''}`;
              onChange(`${prefix}${inserted}${suffix}`);
              requestAnimationFrame(() => {
                textAreaRef.current?.focus({ preventScroll: true });
                textAreaRef.current?.setSelectionRange(
                  prefix.length + inserted.length,
                  prefix.length + inserted.length
                );
              });
            }}
          />

          <TemplateSuggestions
            suggestions={suggestions}
            selectedIndex={selectedIndex}
            loading={isLoadingSuggestions}
            hasMorePages={hasMorePages}
            isOpen={showSuggestions}
            onClose={handleClose}
            onSelect={handleTemplateSelect}
            onLoadMore={fetchMore}
            onCreateTemplate={() => setIsTemplateModalOpen(true)}
          />
        </div>

        <TemplateModal
          isOpen={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
        />
      </>
    );
  }
);

MessageInput.displayName = 'MessageInput';
